import {
  FormProvider,
  type SubmissionResult,
  getFormProps,
  useForm,
} from "@conform-to/react";
import { Trans, t } from "@lingui/macro";
import { Music } from "@winamp/icons/Music.js";
import { Spotify } from "@winamp/icons/Spotify.js";
import { Upload } from "@winamp/icons/Upload.js";
import { Button } from "@winamp/ui/components/Button/Button.js";
import { DialogBack } from "@winamp/ui/components/Dialog/DialogBack.js";
import { DialogBody } from "@winamp/ui/components/Dialog/DialogBody.js";
import { DialogClose } from "@winamp/ui/components/Dialog/DialogClose.js";
import { DialogTitle } from "@winamp/ui/components/Dialog/DialogTitle.js";
import { DialogWrapper } from "@winamp/ui/components/Dialog/DialogWrapper.js";
import { Dropzone } from "@winamp/ui/components/Dropzone/Dropzone.js";
import { DropzoneSubtitle } from "@winamp/ui/components/Dropzone/DropzoneSubtitle.js";
import { DropzoneTitle } from "@winamp/ui/components/Dropzone/DropzoneTitle.js";
import { Fieldset } from "@winamp/ui/components/Fieldset/Fieldset.js";
import { FormErrorsMessage } from "@winamp/ui/components/Fieldset/FormErrorsMessage.js";
import { useToastContext } from "@winamp/ui/components/Toast/use-toast-context.js";
import { classNames } from "@winamp/ui/utils/classNames.js";
import { getValibotConstraint, parseWithValibot } from "conform-to-valibot";
import { useEffect, useRef, useState } from "react";
import { useFetcher } from "react-router";
import { $path } from "safe-routes";
import type * as v from "valibot";
import { SpotifyImportWarningMessages } from "~/components/SpotifyImport/SpotifyImportWarningMessages";
import { Retryable } from "~/modules/retry/Retryable";
import { useMediaDropzoneOptions } from "~/routes/_app+/media+/hooks/use-media-dropzone-options";
import type {
  action as MediaUploadAction,
  loader as MediaUploadLoader,
} from "~/routes/_app+/media_+/new.ts";
import { getArtistLinkSchema } from "~/routes/resources+/artists.$artistId.links.$linkId";
import {
  type DialogProps,
  DialogRetryPlaceholder,
  useDialogDataFetcher,
} from "..";
import { SpotifyCombobox } from "../../../../components/SpotifyArtistCombobox";
import { useOptionalAppContext, useOptionalAuth } from "../../../auth/hooks";
import { patchForm } from "../../../conform/helpers";
import { type Schema, key } from "./config";

export enum Step {
  Options = "options",
  Spotify = "spotify",
}

export function ViewBody({
  hashParams,
  params,
  loadData,
  reloading,
  artistUid,
  labelUid,
  ...props
}: {
  params: v.InferOutput<ReturnType<typeof Schema>> | undefined;
  artistUid: string;
  labelUid: string;
} & DialogProps<true>) {
  const initialStep = hashParams.get("step");
  const context = useOptionalAppContext();
  const { addToast } = useToastContext();
  const { user } = useOptionalAuth() ?? {};

  const { data } = useDialogDataFetcher<typeof MediaUploadLoader>(
    artistUid &&
      labelUid &&
      $path("/labels/:labelId/artists/:artistId/media/new", {
        artistId: artistUid,
        labelId: labelUid,
      }),
    {
      key,
      when: props.open && !!artistUid && !!labelUid,
      deps: [props.open, artistUid, labelUid],
    },
  );

  const isVerifiedKYC = user?.kyc.status === "Verified";
  const initialStepRef = useRef(
    initialStep === Step.Spotify ? Step.Spotify : Step.Options,
  );
  const [step, setStep] = useState<Step>(initialStepRef.current);
  const [lastResult, setLastResult] = useState<
    SubmissionResult<Array<string>> | null | undefined
  >(null);
  const dropzoneContainerRef = useRef<HTMLDivElement | null>(null);
  const isBlackListed = data?.artist?.data?.data?.isKyaBlacklisted;
  const hasDistributionLinks =
    (data?.artist?.data?.data?.distributionLinks?.length ?? 0) > 0;
  const isOldArtist = data?.artist?.data?.data?.kyaStatus?.id === 0 || false;

  const neverImportedCatalog = !hasDistributionLinks;
  const isNewArtist = !isOldArtist;

  const handleClose = () => {
    props.onClose?.(true);
  };

  const { dropzoneOptions, dropErrors } = useMediaDropzoneOptions({
    onDropSuccess: () => {
      handleClose();
    },
    showSuccessToast: true,
  });

  const fetcher = useFetcher<typeof MediaUploadAction>({
    key: `${artistUid}-createSpotifyLink-action`,
  });

  useEffect(() => {
    if (props.open) {
      setStep(initialStepRef.current);
    }
  }, [props.open]);

  const formAction = $path("/resources/artists/:artistId/links", {
    artistId: artistUid,
  });

  const isPending = fetcher.state !== "idle";

  const MediaUploadSchema = getArtistLinkSchema();

  useEffect(() => {
    setLastResult(fetcher.data);

    if (fetcher.data?.status === "success") {
      if ("result" in fetcher.data) {
        handleClose();
        addToast({
          type: "success",
          title: t`Your Spotify catalog is being imported`,
          message: t`This can take a moment.`,
        });
      }
    }
  }, [fetcher.data]);

  const nextStep = () => {
    setStep(Step.Spotify);
  };

  const [form, fields] = useForm({
    defaultValue: {
      url: "",
      distributionLink: true,
    },
    id: [key, artistUid, props.open].join("-"),
    lastResult,
    constraint: getValibotConstraint(MediaUploadSchema),
    shouldValidate: "onBlur",
    onValidate: ({ formData }) => {
      const validation = parseWithValibot(formData, {
        schema: MediaUploadSchema,
      });
      return validation;
    },
  });

  const onSpotifyArtistChange = (
    artist: {
      id: string;
      name: string;
      url: string;
      images: Array<{ url: string; width: number; height: number }>;
    } | null,
  ) => {
    if (!artist) return;
    patchForm(form, {
      url: artist.url,
      distributionLink: true,
    });
  };

  return (
    <DialogWrapper
      {...props}
      size="xl"
      open={props.open}
      onClose={handleClose}
      className="sm:pb-16"
    >
      <Retryable
        condition={true}
        fallback={({ shouldRetry, ...retryProps }) => (
          <DialogRetryPlaceholder dialogKey={key} {...retryProps} />
        )}
        onRetry={() => loadData?.()}
        isRetrying={reloading}
      >
        <div className="flex flex-col">
          <div
            className={classNames("flex justify-end", {
              "justify-between":
                step === Step.Spotify && initialStep !== Step.Spotify,
            })}
          >
            {step === Step.Spotify &&
              initialStep !== Step.Spotify &&
              props.open && (
                <DialogBack onClick={() => setStep(Step.Options)} />
              )}
            <DialogClose onClick={handleClose} />
          </div>

          {step === Step.Options && (
            <div id="media-upload-01">
              <DialogTitle variant="centered">
                <Trans>Add media</Trans>
              </DialogTitle>

              <DialogBody className="flex flex-col gap-10">
                <div ref={dropzoneContainerRef}>
                  <Dropzone
                    options={dropzoneOptions}
                    className="w-full max-sm:hidden h-44"
                    variant={
                      dropErrors && dropErrors.length > 0 ? "error" : "default"
                    }
                  >
                    <Music
                      className={classNames(
                        dropErrors && dropErrors.length > 0 && "text-error",
                      )}
                    />
                    {dropErrors && dropErrors.length > 0 ? (
                      <DropzoneTitle>
                        <Trans>Wrong Format</Trans>
                      </DropzoneTitle>
                    ) : (
                      <>
                        <DropzoneTitle>
                          <Trans>
                            Drag & Drop Media or
                            <br />
                            <span className="text-link">Click to upload</span>
                          </Trans>
                        </DropzoneTitle>
                      </>
                    )}
                    <DropzoneSubtitle>
                      wav, flac, pcm, mp3, aac, mov, mp4, jpeg, png, gif, srt,
                      lrc
                    </DropzoneSubtitle>
                  </Dropzone>
                </div>
                <Button
                  variant="secondary"
                  size="xl"
                  className="sm:hidden w-full"
                  onClick={() => {
                    const fileInput =
                      dropzoneContainerRef.current?.querySelector(
                        'input[type="file"]',
                      ) as HTMLInputElement | null;
                    if (fileInput) {
                      fileInput.click();
                    }
                  }}
                >
                  <Upload /> Upload media
                </Button>
                {neverImportedCatalog && isNewArtist && (
                  <div className="flex flex-col gap-6">
                    <p className="flex items-center justify-center text-normal text-sm/5 gap-0.5">
                      <Trans>
                        or link your Spotify account to import your entire
                        catalog
                      </Trans>
                      <Spotify />
                    </p>
                  </div>
                )}
              </DialogBody>
              {neverImportedCatalog && isNewArtist && (
                <>
                  <Button
                    variant="primary"
                    size="xl"
                    className="w-full mt-5"
                    onClick={nextStep}
                    disabled={isBlackListed || !isVerifiedKYC}
                  >
                    <Trans>Import catalog from Spotify </Trans>
                  </Button>
                  <SpotifyImportWarningMessages
                    isBlackListed={isBlackListed}
                    isVerifiedKYC={isVerifiedKYC}
                  />
                </>
              )}
            </div>
          )}

          {step === Step.Spotify && (
            <div id="spotify-import">
              <DialogTitle variant="centered">
                <Trans>Import catalog from Spotify</Trans>
              </DialogTitle>

              <DialogBody className="flex flex-col gap-6">
                <div className="flex flex-col gap-4">
                  <p className="text-sm my-6">
                    <Trans>
                      With this connection, we will import your entire catalog
                      as draft releases. After that, you'll need to upload the
                      original audio files, complete any missing information,
                      and submit the release to our team.
                    </Trans>
                  </p>
                  <FormProvider context={form.context}>
                    <fetcher.Form
                      method="post"
                      action={formAction}
                      {...getFormProps(form)}
                    >
                      <input
                        name="distributionLink"
                        type="hidden"
                        value="true"
                      />
                      <Fieldset disabled={isPending} className="w-full">
                        <SpotifyCombobox
                          onChange={onSpotifyArtistChange}
                          spotifyUrlField={fields.url}
                          search={context?.artist?.name}
                        />
                      </Fieldset>
                    </fetcher.Form>
                  </FormProvider>
                  <FormErrorsMessage errors={form.errors} />
                </div>
              </DialogBody>
              <Button
                variant="primary"
                size="xl"
                className="w-full mt-10"
                form={form.id}
                type="submit"
                disabled={isPending}
                loading={isPending}
              >
                {isPending ? (
                  <Trans>Synchronising artist with Spotify</Trans>
                ) : (
                  <Trans>Import catalog from Spotify</Trans>
                )}
              </Button>
            </div>
          )}
        </div>
      </Retryable>
    </DialogWrapper>
  );
}
